// Enhanced version of CaptureEquipment.js with equipment type selection
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
  Tooltip,
  Tab,
  Tabs,
  Input,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  CameraAlt as CameraIcon,
  FlipCameraAndroid as FlipIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  FlashOn as FlashOnIcon,
  FlashOff as FlashOffIcon,
  Brightness5 as BrightnessIcon,
  Upload as UploadIcon,
  Photo as PhotoIcon,
} from "@mui/icons-material";
import ErrorModal from "../common/ErrorModal";

const CaptureEquipment = () => {
  const { id: jobId } = useParams();
  const navigate = useNavigate();

  // Add a tab state to switch between camera and upload
  const [captureMethod, setCaptureMethod] = useState("camera");
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const [processingStatus, setProcessingStatus] = useState("");

  // Add equipment type state
  const [equipmentType, setEquipmentType] = useState("");
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);

  // Error modal state
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState("environment"); // 'environment' for back camera
  const [flashAvailable, setFlashAvailable] = useState(false);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  const [lowLightDetected, setLowLightDetected] = useState(false);
  const [focusPoint, setFocusPoint] = useState(null);

  // Equipment types array
  const equipmentTypes = [
    "Tank Water Heater",
    "Tankless Water Heater",
    "Water Softener",
    "Straight Cool Condenser",
    "Heat Pump Condenser",
    "Air Handler",
    "Furnace",
    "Evaporator Coil",
  ];

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const trackRef = useRef(null); // Reference to keep track of video track for flash control

  // Fetch job details on component mount
  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const res = await axios.get(`/jobs/${jobId}`);
        setJob(res.data.job);
      } catch (error) {
        setError("Failed to load job details. Please try again.");
        console.error("Error fetching job:", error);
      } finally {
        setInitializing(false);
      }
    };

    fetchJobDetails();

    // Cleanup function to stop camera when component unmounts
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [jobId]);

  // Start camera when component loads if camera method is selected
  useEffect(() => {
    if (!initializing && !error && captureMethod === "camera") {
      startCamera();
    }
  }, [initializing, error, captureMethod]);

  // Detect low light conditions
  useEffect(() => {
    if (!videoRef.current || !stream || captureMethod !== "camera") return;

    const checkLightLevel = () => {
      if (!canvasRef.current || !videoRef.current) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      // Make canvas match video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw current video frame to canvas
      ctx.drawImage(video, 0, 0);

      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Calculate average brightness
      let totalBrightness = 0;
      for (let i = 0; i < data.length; i += 4) {
        // Convert RGB to brightness using standard luminance formula
        const brightness =
          0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        totalBrightness += brightness;
      }

      const avgBrightness = totalBrightness / (data.length / 4);
      // If average brightness is below threshold, suggest flash
      if (avgBrightness < 70) {
        // Threshold can be adjusted
        setLowLightDetected(true);
      } else {
        setLowLightDetected(false);
      }
    };

    // Check light level every second
    const lightCheckInterval = setInterval(checkLightLevel, 1000);

    return () => {
      clearInterval(lightCheckInterval);
    };
  }, [stream, captureMethod]);

  const startCamera = async () => {
    try {
      // Stop any existing stream
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      // Get camera stream with specified facing mode
      const constraints = {
        video: {
          facingMode,
          advanced: [{ torch: flashEnabled }], // Request torch/flash
          // Enable manual focus capability if available
          focusMode: "manual",
        },
        audio: false,
      };

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);

      setStream(newStream);

      // Store the video track reference for flash control
      const videoTrack = newStream.getVideoTracks()[0];
      trackRef.current = videoTrack;

      // Check if flash/torch is supported
      if (videoTrack) {
        const capabilities = videoTrack.getCapabilities();
        setFlashAvailable(capabilities && capabilities.torch);
      }

      // Connect the stream to the video element
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      setError(
        "Failed to access camera. Please check permissions and try again."
      );
    }
  };

  const handleEquipmentTypeChange = (e) => {
    setEquipmentType(e.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setCaptureMethod(newValue);
    // Clear any captured/uploaded images when switching methods
    setCapturedImage(null);
    setUploadedImage(null);
    setProcessedData(null);
    setProcessingStatus("");

    // If switching to camera, start the camera
    if (newValue === "camera") {
      startCamera();
    } else {
      // If switching away from camera, stop the camera
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      }
    }
  };

  // Handle tap to focus - improved implementation
  const handleTapToFocus = async (e) => {
    if (!trackRef.current) return;

    try {
      // Show focus indicator regardless of focus capability
      const videoElement = videoRef.current;
      const rect = videoElement.getBoundingClientRect();

      // Set focus point for visual indicator
      setFocusPoint({ x: e.clientX - rect.left, y: e.clientY - rect.top });

      // Get capabilities
      const capabilities = trackRef.current.getCapabilities();

      // Try different focus modes in sequence
      if (capabilities && capabilities.focusMode) {
        // Calculate normalized coordinates (0-1)
        const x = (e.clientX - rect.left) / videoElement.clientWidth;
        const y = (e.clientY - rect.top) / videoElement.clientHeight;

        // Try to set point-of-interest if supported
        if (capabilities.pointsOfInterest) {
          try {
            await trackRef.current.applyConstraints({
              advanced: [
                {
                  pointsOfInterest: [{ x, y }],
                },
              ],
            });
          } catch (err) {
            console.log(
              "Point of interest not fully supported, trying other modes"
            );
          }
        }

        // Try different focus modes in order of preference
        const focusModes = ["single-shot", "continuous", "manual", "auto"];
        for (const mode of focusModes) {
          if (capabilities.focusMode.includes(mode)) {
            try {
              await trackRef.current.applyConstraints({
                focusMode: mode,
              });

              // If this is single-shot mode, trigger an immediate focus
              if (mode === "single-shot") {
                // Some devices need a small delay to ensure focus works properly
                setTimeout(async () => {
                  try {
                    // Re-apply constraints to trigger focus
                    await trackRef.current.applyConstraints({
                      focusMode: mode,
                    });
                  } catch (err) {
                    // Ignore errors in delayed focus
                  }
                }, 100);
              }

              break; // Stop after finding first supported mode
            } catch (err) {
              console.log(`Focus mode ${mode} failed, trying next one`);
            }
          }
        }
      }

      // Always show focus animation for better UX, even if actual focus failed
      setTimeout(() => {
        setFocusPoint(null);
      }, 1000);
    } catch (err) {
      console.error("Error setting focus:", err);
      // Still show and hide focus indicator for better UX
      setTimeout(() => {
        setFocusPoint(null);
      }, 1000);
    }
  };

  const toggleFlash = async () => {
    if (!trackRef.current || !flashAvailable) return;

    try {
      const newFlashState = !flashEnabled;
      await trackRef.current.applyConstraints({
        advanced: [{ torch: newFlashState }],
      });
      setFlashEnabled(newFlashState);
    } catch (err) {
      console.error("Error toggling flash:", err);
    }
  };

  const flipCamera = () => {
    const newFacingMode = facingMode === "environment" ? "user" : "environment";
    setFacingMode(newFacingMode);
    setFlashEnabled(false); // Reset flash when flipping camera

    // Restart camera with new facing mode
    startCamera();
  };

  const captureImage = () => {
    if (!equipmentType) {
      setTypeDialogOpen(true);
      return;
    }

    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the current video frame on the canvas
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to base64 image
    const imageData = canvas.toDataURL("image/jpeg", 0.95); // Higher quality
    setCapturedImage(imageData);

    // Stop the camera
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const handleFileSelect = (event) => {
    if (!equipmentType) {
      setTypeDialogOpen(true);
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.match("image.*")) {
      setError("Please select an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => {
    if (!equipmentType) {
      setTypeDialogOpen(true);
      return;
    }
    fileInputRef.current.click();
  };

  const resetCapture = () => {
    if (captureMethod === "camera") {
      setCapturedImage(null);
      setProcessedData(null);
      setProcessingStatus("");
      startCamera();
    } else {
      setUploadedImage(null);
      setProcessedData(null);
      setProcessingStatus("");
    }
  };

  const showErrorModal = (title, message) => {
    setErrorModal({
      open: true,
      title,
      message,
    });
  };

  const closeErrorModal = () => {
    setErrorModal({
      open: false,
      title: "",
      message: "",
    });
  };

  const processImage = async () => {
    if (!equipmentType) {
      setTypeDialogOpen(true);
      return;
    }

    const imageToProcess =
      captureMethod === "camera" ? capturedImage : uploadedImage;
    if (!imageToProcess) return;

    setLoading(true);
    setProcessingStatus("Analyzing equipment data with AI...");

    try {
      // Send image directly to server for OpenAI vision analysis
      const response = await axios.post("/equipment/process", {
        jobId,
        imageData: imageToProcess,
        equipmentType,
      });

      setProcessedData(response.data.data);
      setProcessingStatus("Done!");

      // Wait 2 seconds and return to dashboard
      setTimeout(() => {
        navigate(`/job/${jobId}`);
      }, 2000);
    } catch (error) {
      console.error("Error processing image:", error);

      // Handle different error types
      if (error.response) {
        const { error: errorType, message } = error.response.data;

        switch (errorType) {
          case "SERVICE_UNAVAILABLE":
            showErrorModal("Service Unavailable", message);
            break;
          case "NO_DATA_DETECTED":
          case "PROCESSING_ERROR":
            showErrorModal("Processing Error", message);
            break;
          default:
            showErrorModal(
              "Error",
              "We were unable to use that image, please try again."
            );
        }
      } else {
        showErrorModal(
          "Error",
          "We were unable to use that image, please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate(`/job/${jobId}`);
  };

  if (initializing) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            sx={{ mr: 2 }}
            onClick={goBack}
          >
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Capture Equipment
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {job && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              {job.name}
            </Typography>

            {/* Equipment Type Selection */}
            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel id="equipment-type-label">Equipment Type</InputLabel>
              <Select
                labelId="equipment-type-label"
                id="equipment-type"
                value={equipmentType}
                label="Equipment Type"
                onChange={handleEquipmentTypeChange}
              >
                {equipmentTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body1" color="text.secondary">
              Take pictures of equipment nameplates to capture data.
            </Typography>
          </Box>
        )}

        {/* Add tabs for choosing between camera or upload */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={captureMethod}
            onChange={handleTabChange}
            variant="fullWidth"
          >
            <Tab
              label="Camera"
              value="camera"
              icon={<CameraIcon />}
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
            <Tab
              label="Upload"
              value="upload"
              icon={<UploadIcon />}
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
          </Tabs>
        </Box>

        {captureMethod === "camera" &&
          !capturedImage &&
          lowLightDetected &&
          flashAvailable && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Low light detected. Turn on flash for better results.
            </Alert>
          )}

        <Paper
          elevation={3}
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {captureMethod === "camera" && !capturedImage ? (
            // Camera view
            <Box
              sx={{
                width: "100%",
                position: "relative",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                onClick={handleTapToFocus}
                style={{
                  width: "100%",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              />

              {/* Focus point indicator */}
              {focusPoint && (
                <Box
                  sx={{
                    position: "absolute",
                    top: focusPoint.y - 20,
                    left: focusPoint.x - 20,
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    border: "2px solid white",
                    pointerEvents: "none",
                    animation: "pulse 1s",
                    "@keyframes pulse": {
                      "0%": { transform: "scale(1)", opacity: 1 },
                      "100%": { transform: "scale(1.5)", opacity: 0 },
                    },
                  }}
                />
              )}

              <Box
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: 0,
                  right: 0,
                  display: "flex",
                  justifyContent: "space-around",
                  zIndex: 2,
                }}
              >
                <IconButton
                  onClick={flipCamera}
                  sx={{
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.7)",
                    },
                  }}
                >
                  <FlipIcon />
                </IconButton>

                {flashAvailable && (
                  <IconButton
                    onClick={toggleFlash}
                    sx={{
                      backgroundColor: "rgba(0,0,0,0.5)",
                      color: flashEnabled ? "yellow" : "white",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.7)",
                      },
                    }}
                  >
                    {flashEnabled ? <FlashOnIcon /> : <FlashOffIcon />}
                  </IconButton>
                )}

                <IconButton
                  onClick={captureImage}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.8)",
                    color: "black",
                    p: 2,
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,1)",
                    },
                  }}
                >
                  <CameraIcon fontSize="large" />
                </IconButton>
              </Box>

              {/* Capture guide overlay */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  border: "2px dashed rgba(255,255,255,0.5)",
                  margin: "25px",
                  pointerEvents: "none",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    top: -18,
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "white",
                    padding: "2px 8px",
                    borderRadius: "4px",
                  }}
                >
                  Align nameplate within box
                </Typography>
              </Box>
            </Box>
          ) : captureMethod === "upload" && !uploadedImage ? (
            // Upload view
            <Box sx={{ width: "100%", textAlign: "center" }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />

              <Box
                sx={{
                  border: "2px dashed #ccc",
                  borderRadius: 2,
                  py: 6,
                  px: 2,
                  mb: 3,
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
                onClick={triggerFileInput}
              >
                <PhotoIcon
                  sx={{
                    fontSize: 60,
                    color: "text.secondary",
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Upload Equipment Photo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click to browse or drag and drop
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={triggerFileInput}
                fullWidth
              >
                Select Photo
              </Button>
            </Box>
          ) : (
            // Captured or uploaded image view
            <Box sx={{ width: "100%" }}>
              <img
                src={captureMethod === "camera" ? capturedImage : uploadedImage}
                alt="Equipment"
                style={{ width: "100%", borderRadius: "4px" }}
              />

              {loading ? (
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {processingStatus || "Processing..."}
                  </Typography>
                </Box>
              ) : processedData ? (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Extracted Data
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Equipment Type
                        </Typography>
                        <Typography variant="body1">{equipmentType}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Brand
                        </Typography>
                        <Typography variant="body1">
                          {processedData.brand || "Not detected"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Model
                        </Typography>
                        <Typography variant="body1">
                          {processedData.modelNumber || "Not detected"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Serial Number
                        </Typography>
                        <Typography variant="body1">
                          {processedData.serialNumber || "Not detected"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Manufactured Date
                        </Typography>
                        <Typography variant="body1">
                          {processedData.manufacturedDate || "Not detected"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={resetCapture}
                  >
                    {captureMethod === "camera" ? "Retake" : "Choose Another"}
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckIcon />}
                    onClick={processImage}
                  >
                    Process Image
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>

        {/* Hidden canvas for image capture and light detection */}
        <canvas ref={canvasRef} style={{ display: "none" }} />

        {/* Equipment Type Dialog */}
        <Dialog open={typeDialogOpen} onClose={() => setTypeDialogOpen(false)}>
          <DialogTitle>Equipment Type Required</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Please select an equipment type before capturing equipment data.
            </DialogContentText>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="dialog-equipment-type-label">
                Equipment Type
              </InputLabel>
              <Select
                labelId="dialog-equipment-type-label"
                id="dialog-equipment-type"
                value={equipmentType}
                label="Equipment Type"
                onChange={handleEquipmentTypeChange}
              >
                {equipmentTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTypeDialogOpen(false)} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Error Modal */}
        <ErrorModal
          open={errorModal.open}
          onClose={closeErrorModal}
          title={errorModal.title}
          message={errorModal.message}
        />

        {/* Helpful instructions - show whether in camera or upload mode, but not when image is captured */}
        {!capturedImage && !uploadedImage && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tips for Better Results
              </Typography>
              <Typography variant="body2" paragraph>
                • Ensure the nameplate is well-lit and clearly visible
              </Typography>
              <Typography variant="body2" paragraph>
                • Remove any dirt or debris from the nameplate
              </Typography>
              {captureMethod === "camera" ? (
                <>
                  <Typography variant="body2" paragraph>
                    • Tap on the nameplate to focus the camera
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Hold the camera steady and parallel to the nameplate
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" paragraph>
                    • Use a clear, high-resolution photo
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Avoid blurry or low-light images
                  </Typography>
                </>
              )}
              <Typography variant="body2">
                • Make sure the entire nameplate is visible in the image
              </Typography>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default CaptureEquipment;
